import { Link } from "react-router-dom";
import {
    ArrowRight,
    ArrowLeft,
    Handshake,
    Target,
    TrendingUp,
    ShieldCheck,
    UserCog,
    ScanSearch,
    Network as NetworkIcon,
    Compass,
} from "lucide-react";
import Reveal from "../components/Reveal";
import WaitlistForm from "../components/WaitlistForm";

function Eyebrow({ children, light }) {
    return (
        <div
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold ${
                light ? "text-[#34D399]" : "text-[#10B981]"
            }`}
        >
            <span
                className={`w-6 h-px ${
                    light ? "bg-[#34D399]" : "bg-[#10B981]"
                }`}
            />
            {children}
        </div>
    );
}

function Hero() {
    return (
        <section
            data-testid="mechanics-hero"
            className="relative pt-48 sm:pt-56 pb-20 sm:pb-28 overflow-hidden mx-mint-gradient"
        >
            <div className="absolute inset-0 mx-grid-bg opacity-60 pointer-events-none" />
            <div className="absolute -top-20 right-0 w-[520px] h-[520px] rounded-full bg-[#10B981]/10 blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <Reveal>
                    <Link
                        to="/"
                        data-testid="back-home"
                        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#070F1D] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to home
                    </Link>
                </Reveal>
                <Reveal delay={0.05}>
                    <div className="mt-8">
                        <Eyebrow>For independent mechanics</Eyebrow>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1
                        data-testid="mechanics-hero-headline"
                        className="mt-5 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] max-w-4xl text-[#070F1D]"
                    >
                        Stop chasing leads.
                        <br />
                        <span className="text-[#10B981]">
                            Start building a network.
                        </span>
                    </h1>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">
                        MechXpress connects independent mechanics to customers
                        — and to each other — creating a referral-powered
                        network where everyone wins.
                    </p>
                </Reveal>
                <Reveal delay={0.2}>
                    <a
                        href="#mechanic-waitlist"
                        data-testid="mechanics-hero-cta"
                        className="mt-10 inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-full px-8 py-4 shadow-[0_16px_30px_-10px_rgba(16,185,129,0.5)] hover:shadow-[0_20px_40px_-10px_rgba(16,185,129,0.6)] transition-all group"
                    >
                        Join the waitlist as a mechanic
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                </Reveal>
            </div>
        </section>
    );
}

function Problem() {
    return (
        <section className="relative py-24 sm:py-32 bg-white">
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
                <Reveal>
                    <Eyebrow>The Problem</Eyebrow>
                </Reveal>
                <Reveal delay={0.05}>
                    <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight leading-tight max-w-3xl text-[#070F1D]">
                        Being independent shouldn't mean being alone.
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="mt-10 grid md:grid-cols-12 gap-10 items-start">
                        <div className="md:col-span-7 text-lg text-slate-700 leading-relaxed">
                            You're great at what you do. But as a solo or
                            small-shop mechanic, you're turning away jobs
                            outside your specialty, losing customers you can't
                            schedule in time, and competing with dealerships
                            that have brand recognition you don't.
                            <br />
                            <br />
                            Meanwhile, there's another independent mechanic
                            five miles away who specializes in exactly the
                            work you're turning down — and they're dealing with
                            the same problem in reverse.
                        </div>
                        <div className="md:col-span-5">
                            <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-gradient-to-br from-[#070F1D] to-[#0C1A33] aspect-[4/5] p-8 flex flex-col justify-end">
                                <div className="absolute inset-0 mx-dark-gradient opacity-80" />
                                <div className="relative">
                                    <div className="text-xs uppercase tracking-[0.22em] text-[#34D399] font-bold">
                                        The reality
                                    </div>
                                    <div className="mt-3 text-2xl sm:text-3xl font-black text-white leading-tight">
                                        Independent ≠ isolated.
                                    </div>
                                    <p className="mt-4 text-slate-300 text-sm leading-relaxed">
                                        You shouldn't lose work just because
                                        you can't clone yourself.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

const NETWORK_CARDS = [
    {
        icon: Handshake,
        title: "Earn from referrals",
        desc: "When you send a job to another mechanic in the network, you build reciprocity. More referrals out means more referrals back. The network rewards generosity.",
    },
    {
        icon: Target,
        title: "Cover your gaps without faking expertise",
        desc: "That hybrid battery diagnostic or ECU reflash? Refer it out confidently instead of fumbling through it. Your customer gets better service and comes back to you for what you're actually great at.",
    },
    {
        icon: TrendingUp,
        title: "Grow without marketing",
        desc: "The network does the customer acquisition for you. Show up, do great work, maintain your rating — and the jobs keep coming.",
    },
];

function NetworkSection() {
    return (
        <section className="relative py-24 sm:py-32 bg-slate-50 border-y border-slate-200">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                    <Reveal>
                        <Eyebrow>The Network</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight leading-tight text-[#070F1D]">
                            A mechanic-first network
                            <br />
                            <span className="text-[#10B981]">
                                with customer access.
                            </span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                            MechXpress isn't a typical marketplace that treats
                            mechanics like inventory. We built the platform
                            around you. When a customer describes their
                            problem, we match them with the right mechanic
                            based on specialty, location, and availability. But
                            here's what makes it different: when a job isn't
                            your fit, you refer it to a trusted mechanic in the
                            network — and they do the same for you.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-16 grid md:grid-cols-3 gap-6">
                    {NETWORK_CARDS.map((c, i) => (
                        <Reveal key={c.title} delay={i * 0.08}>
                            <div
                                data-testid={`network-card-${i}`}
                                className="group h-full bg-white border border-slate-200 rounded-2xl p-8 hover:border-[#10B981]/40 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(16,185,129,0.25)] transition-all duration-500"
                            >
                                <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 grid place-items-center mb-6 group-hover:bg-[#10B981] transition-colors">
                                    <c.icon className="w-6 h-6 text-[#10B981] group-hover:text-white transition-colors" />
                                </div>
                                <h3 className="text-xl font-bold text-[#070F1D]">
                                    {c.title}
                                </h3>
                                <p className="mt-3 text-slate-600 leading-relaxed">
                                    {c.desc}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

const STEPS = [
    {
        n: "01",
        icon: ShieldCheck,
        title: "Apply & get verified",
        desc: "Submit your credentials, specialty, and service area. We verify independently so customers trust you from day one.",
    },
    {
        n: "02",
        icon: UserCog,
        title: "Set your profile",
        desc: "List your specialties, pricing, availability, and service radius. You control everything.",
    },
    {
        n: "03",
        icon: ScanSearch,
        title: "Start getting matched",
        desc: "Customers describe their problem, and our system matches them to you. Accept jobs that fit, refer the ones that don't.",
    },
    {
        n: "04",
        icon: NetworkIcon,
        title: "Build your network",
        desc: "As you refer and get referred, your network strengthens. More connections = more consistent work.",
    },
];

function HowItWorksMechanics() {
    return (
        <section className="relative py-24 sm:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                    <Reveal>
                        <Eyebrow>How it works for mechanics</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight text-[#070F1D]">
                            Simple to start.{" "}
                            <span className="text-[#10B981]">
                                Built to scale with you.
                            </span>
                        </h2>
                    </Reveal>
                </div>

                <div className="mt-14 grid md:grid-cols-2 gap-6">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.07}>
                            <div
                                data-testid={`mech-step-${i}`}
                                className="group h-full bg-white border border-slate-200 rounded-2xl p-8 hover:border-[#10B981]/40 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(16,185,129,0.25)] transition-all duration-500 flex gap-6"
                            >
                                <div className="shrink-0">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 grid place-items-center group-hover:bg-[#10B981] transition-colors">
                                        <s.icon className="w-6 h-6 text-[#10B981] group-hover:text-white transition-colors" />
                                    </div>
                                </div>
                                <div>
                                    <span className="font-mono text-xs tracking-wider text-slate-400">
                                        STEP {s.n}
                                    </span>
                                    <h3 className="mt-2 text-xl sm:text-2xl font-bold text-[#070F1D]">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2 text-slate-600 leading-relaxed">
                                        {s.desc}
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Vision() {
    return (
        <section className="relative py-24 sm:py-32 bg-[#070F1D] text-white overflow-hidden">
            <div className="absolute inset-0 mx-dark-gradient pointer-events-none" />
            <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
                <Reveal>
                    <Eyebrow light>The Vision</Eyebrow>
                </Reveal>
                <Reveal delay={0.05}>
                    <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight leading-tight max-w-4xl text-white">
                        We're building the mechanic's network —{" "}
                        <span className="text-[#34D399]">
                            not another gig app.
                        </span>
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-8 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-3xl">
                        Gig platforms extract value from workers. We're doing
                        the opposite. MechXpress is designed so that the longer
                        you stay and the more you contribute, the more
                        valuable the platform becomes for you personally. Your
                        referrals, your ratings, your specialization — that's
                        your moat. We just give you the infrastructure to use
                        it.
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="mt-10 flex items-center gap-3 text-[#34D399]">
                        <Compass className="w-5 h-5" />
                        <span className="text-sm font-bold uppercase tracking-wider">
                            Mechanic-owned growth
                        </span>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

function FinalCTA() {
    return (
        <section
            id="mechanic-waitlist"
            data-testid="mechanic-waitlist-section"
            className="relative py-24 sm:py-32 overflow-hidden bg-white"
        >
            <div className="absolute inset-0 mx-mint-gradient opacity-80 pointer-events-none" />
            <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
                <div className="text-center">
                    <Reveal>
                        <h2 className="text-4xl sm:text-6xl font-black tracking-tight leading-[1.05] text-[#070F1D]">
                            Ready to join
                            <br />
                            <span className="text-[#10B981]">
                                the network?
                            </span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
                            MechXpress is launching in the DMV area. Mechanics
                            who join the waitlist early get priority placement
                            and founding member status.
                        </p>
                    </Reveal>
                </div>
                <Reveal delay={0.1}>
                    <div className="mt-12 max-w-lg mx-auto">
                        <WaitlistForm defaultTab="mechanic" />
                    </div>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 text-center text-sm text-slate-500">
                        Currently launching in Washington DC, Maryland, and
                        Virginia.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

export default function ForMechanics() {
    return (
        <main
            data-testid="for-mechanics-page"
            className="relative bg-white"
        >
            <Hero />
            <Problem />
            <NetworkSection />
            <HowItWorksMechanics />
            <Vision />
            <FinalCTA />
        </main>
    );
}
